import { useEffect, useRef } from "react";

/**
 * Subscribes to the realtime timer channel keyed by share_code.
 * Calls onState(timer) whenever the server pushes a new state.
 * Silently no-ops while share_code is falsy.
 */
export function useTimerSocket(shareCode, onState) {
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  useEffect(() => {
    if (!shareCode) return undefined;
    const httpBase = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = httpBase.replace(/^http/, "ws");
    const url = `${wsBase}/api/ws/timers/${shareCode}`;

    let ws;
    let pingId;
    let cancelled = false;
    let retry = 0;

    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(url);
      ws.onopen = () => {
        retry = 0;
        pingId = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "state" && msg.timer) onStateRef.current?.(msg.timer);
        } catch (_) {
          /* ignore non-JSON frames */
        }
      };
      ws.onclose = () => {
        clearInterval(pingId);
        if (cancelled) return;
        // Exponential backoff, capped at 10s
        retry = Math.min(retry + 1, 5);
        setTimeout(connect, Math.min(1000 * 2 ** retry, 10000));
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) { /* noop */ }
      };
    };

    connect();
    return () => {
      cancelled = true;
      clearInterval(pingId);
      try { ws?.close(); } catch (_) { /* noop */ }
    };
  }, [shareCode]);
}
